import { useState, useCallback, useMemo } from 'react'
import { getFieldForStudy } from '@/data/studies'
import { getSubcategoryForStudy, hasSubcategories } from '@/data/studies/subcategoryMapping'

/**
 * Custom hook for managing search filter state
 * Provides centralized filter management with memoized helpers
 *
 * ⭐ CRITICAL: This hook now auto-derives studyField from selected study
 * This fixes the search bug where studyField was missing from API requests
 *
 * @param {Object} initialFilters - Initial filter values (optional)
 * @returns {Object} { filters, setFilter, clearFilters, hasActiveFilters, searchCriteria, isSearchValid }
 */
export const useSearchFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    werktype: '',
    studyLevel: '',
    studyField: '',  // ⭐ ADDED: Auto-derived from study selection
    studySubcategory: '',  // ⭐ ADDED: Auto-derived for HBO Associate Degree, HBO Master, Universiteit
    study: '',
    location: '',
    year: '',
    radius: 10,
    ...initialFilters
  })

  /**
   * Update a single filter value
   * ⭐ CRITICAL: Auto-derives studyField and studySubcategory when study changes
   * Memoized to prevent unnecessary re-renders
   */
  const setFilter = useCallback((name, value) => {
    setFilters(prev => {
      const updated = { ...prev, [name]: value }

      // ⭐ CRITICAL FIX: Auto-derive studyField and studySubcategory when study changes
      if (name === 'study') {
        if (value && prev.studyLevel) {
          const isMBONiveau1 = prev.studyLevel === 'MBO Niveau 1'

          if (isMBONiveau1) {
            // MBO Niveau 1 has no studyField or subcategory (flat array structure)
            updated.studyField = ''
            updated.studySubcategory = ''
            console.log(`🔍 Study changed to "${value}" (MBO Niveau 1 - geen veld/subcategorie)`)
          } else {
            // Check if this level uses subcategories (4-part key structure)
            const usesSubcategories = hasSubcategories(prev.studyLevel)

            if (usesSubcategories) {
              // HBO Associate Degree, HBO Master, Universiteit Bachelor/Master
              const categoryInfo = getSubcategoryForStudy(value, prev.studyLevel)

              if (categoryInfo) {
                updated.studyField = categoryInfo.mainCategory
                updated.studySubcategory = categoryInfo.subcategory

                console.log(`🔍 Study changed to "${value}", derived field: "${categoryInfo.mainCategory}", subcategory: "${categoryInfo.subcategory}"`)
              } else {
                // Study not found in mapping - try fallback to old method
                updated.studyField = getFieldForStudy(value, prev.studyLevel) || ''
                updated.studySubcategory = ''

                console.warn(`⚠️  Study "${value}" not found in subcategory mapping for ${prev.studyLevel}`)
              }
            } else {
              // HBO Bachelor, MBO Niveau 2/3/4 - use old 3-part key method
              const derivedField = getFieldForStudy(value, prev.studyLevel)
              updated.studyField = derivedField || ''
              updated.studySubcategory = ''

              console.log(`🔍 Study changed to "${value}", derived field: "${derivedField || '(niet gevonden)'}"`)
            }
          }
        } else {
          // Clear field and subcategory if study is cleared
          updated.studyField = ''
          updated.studySubcategory = ''
        }

        // Also clear year when study changes
        updated.year = ''
      }

      // Clear dependent fields when study level changes
      if (name === 'studyLevel') {
        updated.study = ''
        updated.studyField = ''
        updated.studySubcategory = ''
        updated.year = ''
      }

      return updated
    })
  }, [])

  /**
   * Clear all filters to default state
   * Memoized to prevent unnecessary re-renders
   */
  const clearFilters = useCallback(() => {
    setFilters({
      werktype: '',
      studyLevel: '',
      studyField: '',  // ⭐ ADDED
      studySubcategory: '',  // ⭐ ADDED
      study: '',
      location: '',
      year: '',
      radius: 10
    })
  }, [])

  /**
   * Check if any filters are currently active
   * Memoized for performance
   */
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filters.werktype || filters.studyLevel || filters.study ||
      filters.location || filters.year
    )
  }, [filters])

  /**
   * Validate that search criteria is valid
   * ⭐ UPDATED: More flexible - any active filter is valid for search
   * The backend handles partial filters gracefully
   * Memoized for performance
   */
  const isSearchValid = useMemo(() => {
    // At least one filter must be selected
    // Any single filter is enough to perform a search
    return hasActiveFilters
  }, [hasActiveFilters])

  /**
   * Build search criteria object for API/navigation
   * ⭐ CRITICAL: Now includes studyField and studySubcategory (the key fix!)
   * Memoized to prevent unnecessary object recreation
   */
  const searchCriteria = useMemo(() => ({
    studyLevel: filters.studyLevel,
    studyField: filters.studyField,  // ⭐ ADDED: Main category
    studySubcategory: filters.studySubcategory,  // ⭐ ADDED: Subcategory for 4-part keys
    study: filters.study,
    location: filters.location,
    year: filters.year,
    werktype: filters.werktype,
    radius: filters.radius
  }), [filters])

  return {
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    isSearchValid,  // ⭐ ADDED: New validation flag
    searchCriteria
  }
}
